import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

/**
 * A per-account preference, stored on the server so it follows the user
 * across browsers. `legacyKey` is the localStorage key it used to live under:
 * when the account has no value yet, the browser's one is uploaded once.
 */
export function usePref<T>(key: string, fallback: T, legacyKey?: string): [T, (v: T) => void, boolean] {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["pref", key],
    queryFn: () => api<{ value: T | null }>(`/api/v1/me/prefs/${key}`),
    staleTime: 60_000,
  });
  const save = (v: T) => {
    qc.setQueryData(["pref", key], { value: v });
    void api(`/api/v1/me/prefs/${key}`, { method: "PUT", body: JSON.stringify({ value: v }) }).catch(() => qc.invalidateQueries({ queryKey: ["pref", key] }));
  };
  useEffect(() => {
    if (!legacyKey || !q.data || q.data.value !== null) return;
    try {
      const old = localStorage.getItem(legacyKey);
      if (old) {
        save(JSON.parse(old) as T);
        localStorage.removeItem(legacyKey);
      }
    } catch { /* unreadable legacy value: ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, legacyKey]);
  // loaded: writers that build on the current value must wait for it, or a
  // first write would replace the account's list with a one-item one.
  return [q.data?.value ?? fallback, save, q.isSuccess];
}
